import React, { useState, useEffect, useRef } from 'react';

const expertiseOptions = [
  'CSS',
  'PMS',
  'PCS',
  'Judiciary Exam',
  'FPSC One Paper Exams',
  'PPSC',
  'KPPSC',
  'SPSC',
  'BPSC'
];

const subjectsOptions = [
  'English Essay',
    'English Precis and Composition',
    'General Science and Ability',
    'Current Affairs',
    'Pakistan Affairs',
    'Islamic Studies OR Comparative Study of Major Religions (For Non Muslims)',
    'Accountancy and Auditing',
    'Economics',
    'Computer Science',
    'Political Science',
    'International Relations',
    'Physics',
    'Chemistry',
    'Applied Mathematics',
    'Pure Mathematics',
    'Statistics',
    'Geology',
    'Business Administration',
    'Public Administration',
    'Governance and Public Policies',
    'Town Planning and Urban Management',
    'History of Pakistan and India',
    'Islamic History and Culture',
    'British History',
    'European History',
    'History of USA',
    'Gender Studies',
    'Environmental Sciences',
    'Agriculture & Forestry',
    'Botany',
    'Zoology',
    'English Literature',
    'Urdu Literature',
    'Law',
    'Constitutional Law',
    'International Law',
    'Muslim Law & Jurisprudence',
    'Criminology',
    'Philosophy',
    'Journalism & Mass Communication',
    'Psychology',
    'Geography',
    'Sociology',
    'Anthropology',
    'Punjabi',
    'Sindhi',
    'Pashto',
    'Balochi',
    'Persian',
    'Arabic',
    'Urdu',
    'General Knowledge'
];

const SettingsProfessional = ({
  profileData,
  handleInputChange,
  isEditing,
  newExpertise,
  setNewExpertise,
  handleExpertiseAdd,
  handleExpertiseRemove,
  handleExpertiseKeyPress
}) => {
  // Local state for selected subjects and expertise
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedExpertise, setSelectedExpertise] = useState([]);
  
  // Dropdown open/close state
  const [subjectsDropdownOpen, setSubjectsDropdownOpen] = useState(false);
  const [expertiseDropdownOpen, setExpertiseDropdownOpen] = useState(false);
  
  // Refs for detecting outside clicks
  const subjectsDropdownRef = useRef(null);
  const expertiseDropdownRef = useRef(null);
  const expertiseButtonRef = useRef(null);

  // Initialize selections from profileData when it changes
  useEffect(() => {
    if (profileData?.professional?.subjects) {
      const subjectsArray = typeof profileData.professional.subjects === 'string' 
        ? profileData.professional.subjects.split(',').map(s => s.trim()).filter(s => s)
        : Array.isArray(profileData.professional.subjects) 
          ? profileData.professional.subjects 
          : [];
      setSelectedSubjects(subjectsArray);
    }

    if (profileData?.professional?.areaOfExpertise) {
      const expertiseArray = typeof profileData.professional.areaOfExpertise === 'string'
        ? profileData.professional.areaOfExpertise.split(',').map(e => e.trim()).filter(e => e)
        : Array.isArray(profileData.professional.areaOfExpertise)
          ? profileData.professional.areaOfExpertise
          : [];
      setSelectedExpertise(expertiseArray);
    }
  }, [profileData]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subjectsDropdownRef.current && !subjectsDropdownRef.current.contains(event.target)) {
        setSubjectsDropdownOpen(false);
      }
      if (expertiseDropdownRef.current && !expertiseDropdownRef.current.contains(event.target)) {
        setExpertiseDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle subject checkbox change
  const handleSubjectCheckbox = (subject) => {
    let updatedSubjects;
    if (selectedSubjects.includes(subject)) {
      updatedSubjects = selectedSubjects.filter(s => s !== subject);
    } else {
      updatedSubjects = [...selectedSubjects, subject];
    }
    setSelectedSubjects(updatedSubjects);
    handleInputChange('professional', 'subjects', updatedSubjects.join(', '));
  };

  // Handle expertise checkbox change
  const handleExpertiseCheckbox = (expertise) => {
    let updatedExpertise;
    if (selectedExpertise.includes(expertise)) {
      updatedExpertise = selectedExpertise.filter(e => e !== expertise);
    } else {
      updatedExpertise = [...selectedExpertise, expertise];
    }
    setSelectedExpertise(updatedExpertise);
    handleInputChange('professional', 'areaOfExpertise', updatedExpertise.join(', '));
  };

  // Select/Deselect all subjects
  const handleSelectAllSubjects = () => {
    if (selectedSubjects.length === subjectsOptions.length) {
      setSelectedSubjects([]);
      handleInputChange('professional', 'subjects', '');
    } else {
      setSelectedSubjects(subjectsOptions);
      handleInputChange('professional', 'subjects', subjectsOptions.join(', '));
    }
  };

  // Select/Deselect all expertise
  const handleSelectAllExpertise = () => {
    if (selectedExpertise.length === expertiseOptions.length) {
      setSelectedExpertise([]);
      handleInputChange('professional', 'areaOfExpertise', '');
    } else {
      setSelectedExpertise(expertiseOptions);
      handleInputChange('professional', 'areaOfExpertise', expertiseOptions.join(', '));
    }
  };

  // Remove individual subject chip
  const removeSubjectItem = (indexToRemove) => {
    const updated = selectedSubjects.filter((_, index) => index !== indexToRemove);
    setSelectedSubjects(updated);
    handleInputChange('professional', 'subjects', updated.join(', '));
  };

  // Remove individual expertise chip
  const removeExpertiseItem = (indexToRemove) => {
    const updated = selectedExpertise.filter((_, index) => index !== indexToRemove);
    setSelectedExpertise(updated);
    handleInputChange('professional', 'areaOfExpertise', updated.join(', '));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Subjects Dropdown with Checkboxes */}
        <div className="transform transition-all duration-500 ease-out hover:scale-105" style={{ position: 'relative', zIndex: subjectsDropdownOpen ? 9999 : 1 }}>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Subjects</label>
          {isEditing ? (
            <div ref={subjectsDropdownRef} style={{ position: 'relative' }}>
              {/* Dropdown Button */}
              <div
                onClick={() => setSubjectsDropdownOpen(!subjectsDropdownOpen)}
                className="w-full px-4 py-3 border border-gray-300 rounded-2xl bg-white cursor-pointer flex justify-between items-center hover:border-green-500 transition-all duration-300"
                style={{ position: 'relative', zIndex: 2 }}
              >
                <span className="truncate">
                  {selectedSubjects.length > 0 
                    ? `${selectedSubjects.length} subject${selectedSubjects.length > 1 ? 's' : ''} selected` 
                    : 'Select subjects'}
                </span>
                <svg 
                  className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${subjectsDropdownOpen ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Dropdown Menu */}
              {subjectsDropdownOpen && (
                <div 
                  className="absolute w-full mt-2 bg-white border border-gray-300 rounded-2xl shadow-lg max-h-80 overflow-y-auto"
                  style={{ 
                    zIndex: 10000,
                    top: '100%',
                    left: 0
                  }}
                >
                  {/* Select All Option */}
                  <div className="p-3 border-b border-gray-200 bg-gray-50 sticky top-0" style={{ zIndex: 10001 }}>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSubjects.length === subjectsOptions.length}
                        onChange={handleSelectAllSubjects}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Select All</span>
                    </label>
                  </div>
                  
                  {/* Subject Options */}
                  <div className="p-2">
                    {subjectsOptions.map((subject, index) => (
                      <label
                        key={index}
                        className="flex items-center space-x-3 p-2 hover:bg-green-50 rounded-xl cursor-pointer transition-all duration-200"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSubjects.includes(subject)}
                          onChange={() => handleSubjectCheckbox(subject)}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">{subject}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Display selected subjects as chips */}
              {selectedSubjects.length > 0 && (
                <div className="mt-4" style={{ position: 'relative', zIndex: 1 }}>
                  <p className="text-sm font-medium text-gray-600 mb-2">Selected Subjects:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedSubjects.map((subject, index) => (
                      <span
                        key={index}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-xl font-medium flex items-center space-x-1 group hover:bg-blue-200 transition-all duration-200"
                      >
                        <span>{subject}</span>
                        <button
                          type="button"
                          onClick={() => removeSubjectItem(index)}
                          className="text-red-500 hover:text-red-700 transition-colors duration-200 ml-1 w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-100"
                          title="Remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedSubjects.length > 0 ? (
                selectedSubjects.map((subject, index) => (
                  <span
                    key={index}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-xl font-medium transition-all duration-300 hover:scale-105 hover:bg-blue-200"
                  >
                    {subject}
                  </span>
                ))
              ) : (
                <span className="text-gray-500 italic">No subjects selected</span>
              )}
            </div>
          )}
        </div>

        {/* Year of Experience */}
        <div className="transform transition-all duration-500 ease-out hover:scale-105" style={{ position: 'relative', zIndex: 1 }}>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Years of Experience</label>
          <input
            type="number"
            min="0"
            max="50"
            step="1"
            value={profileData.professional.yearsOfExperience || ''}
            onChange={(e) => handleInputChange('professional', 'yearsOfExperience', e.target.value)}
            disabled={!isEditing}
            placeholder="e.g., 5"
            className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* Areas of Expertise - Dropdown with Checkboxes - OPENS UPWARD */}
      <div className="transform transition-all duration-500 ease-out hover:scale-105" style={{ position: 'relative', zIndex: expertiseDropdownOpen ? 9999 : 1 }}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Areas of Expertise</label>
        {isEditing ? (
          <div ref={expertiseDropdownRef} style={{ position: 'relative' }}>
            {/* Dropdown Button */}
            <div
              ref={expertiseButtonRef}
              onClick={() => setExpertiseDropdownOpen(!expertiseDropdownOpen)}
              className="w-full px-4 py-3 border border-gray-300 rounded-2xl bg-white cursor-pointer flex justify-between items-center hover:border-green-500 transition-all duration-300"
              style={{ position: 'relative', zIndex: 2 }}
            >
              <span className="truncate">
                {selectedExpertise.length > 0 
                  ? `${selectedExpertise.length} area${selectedExpertise.length > 1 ? 's' : ''} selected` 
                  : 'Select areas of expertise'}
              </span>
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${expertiseDropdownOpen ? 'transform rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {/* Dropdown Menu - OPENS UPWARD (top ki taraf) */}
            {expertiseDropdownOpen && (
              <div 
                className="absolute w-full bg-white border border-gray-300 rounded-2xl shadow-lg max-h-80 overflow-y-auto"
                style={{ 
                  zIndex: 10000,
                  bottom: 'calc(100% + 4px)', // This makes it open upward
                  left: 0
                }}
              >
                {/* Select All Option */}
                <div className="p-3 border-b border-gray-200 bg-gray-50 sticky top-0" style={{ zIndex: 10001 }}>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedExpertise.length === expertiseOptions.length}
                      onChange={handleSelectAllExpertise}
                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Select All</span>
                  </label>
                </div>
                
                {/* Expertise Options */}
                <div className="p-2">
                  {expertiseOptions.map((option, index) => (
                    <label
                      key={index}
                      className="flex items-center space-x-3 p-2 hover:bg-green-50 rounded-xl cursor-pointer transition-all duration-200"
                    >
                      <input
                        type="checkbox"
                        checked={selectedExpertise.includes(option)}
                        onChange={() => handleExpertiseCheckbox(option)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Display selected expertise as chips */}
            {selectedExpertise.length > 0 && (
              <div className="mt-4" style={{ position: 'relative', zIndex: 1 }}>
                <p className="text-sm font-medium text-gray-600 mb-2">Selected Expertise:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedExpertise.map((item, index) => (
                    <span
                      key={index}
                      className="px-3 py-2 bg-green-100 text-green-700 rounded-xl font-medium flex items-center space-x-1 group hover:bg-green-200 transition-all duration-200"
                    >
                      <span>{item}</span>
                      <button
                        type="button"
                        onClick={() => removeExpertiseItem(index)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200 ml-1 w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-100"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedExpertise.length > 0 ? (
              selectedExpertise.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-2 bg-green-100 text-green-700 rounded-xl font-medium transition-all duration-300 hover:scale-105 hover:bg-green-200"
                >
                  {skill}
                </span>
              ))
            ) : (
              <span className="text-gray-500 italic">No expertise selected</span>
            )}
          </div>
        )}
      </div>

      <div className="transform transition-all duration-500 ease-out hover:scale-105" style={{ position: 'relative', zIndex: 1 }}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Personal Website</label>
        <input
          type="url"
          value={profileData.professional.website || ''}
          onChange={(e) => handleInputChange('professional', 'website', e.target.value)}
          disabled={!isEditing}
          placeholder="https://example.com"
          className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      <div className="transform transition-all duration-500 ease-out hover:scale-105" style={{ position: 'relative', zIndex: 1 }}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">YouTube Video URL</label>
        <input
          type="url"
          value={profileData.professional.videoUrl || ''}
          onChange={(e) => handleInputChange('professional', 'videoUrl', e.target.value)}
          disabled={!isEditing}
          placeholder="https://youtube.com/watch?v=..."
          className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
};

export default SettingsProfessional;